import { VFC, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableSortLabel,
  TableBody,
  TablePagination,
  TableCell,
} from "@mui/material";

import { Tag } from "../../../entity/Tag";
import { ApplicationProtectedRouting } from "../../../Routing";
import { ContractSearchResponse } from "../../contract/api/ContractForm";
import { Utility } from "../../../utils/Utility";

// TODO 消すとモック作成時の他画面でエラーとなるため後ほど削除 使用しない！！
export interface HeadItem {
  key: string;
  value: string;
  isSort?: Boolean;
}
/***************************************************************
 * ヘッダー
 **************************************************************/
interface HeadCell {
  key: keyof ContractSearchResponse;
  value: string;
  isSort?: Boolean;
}
const headCells: readonly HeadCell[] = [
  {
    key: "disclosureCode",
    value: "契約コード",
    isSort: true,
  },
  {
    key: "tenantName",
    value: "テナント名",
    isSort: true,
  },
  {
    key: "companyName",
    value: "企業名",
    isSort: true,
  },
  // {
  //   key: "organizationName",
  //   value: "組織名",
  //   isSort: true,
  // },
  {
    key: "contractDevice",
    value: "契約台数",
    isSort: true,
  },
  {
    key: "contractPeople",
    value: "契約人数",
    isSort: true,
  },
  {
    key: "contractPrice",
    value: "契約価格",
    isSort: true,
  },
  {
    key: "contractStart",
    value: "契約開始日",
    isSort: false,
  },
  {
    key: "contractEnd",
    value: "契約終了日",
    isSort: false,
  },
];
interface HeadTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof ContractSearchResponse
  ) => void;
  order: Order;
  orderBy: string;
}

/***************************************************************
 * ソート
 **************************************************************/
type Order = "asc" | "desc";
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: string | Tag[] },
  b: { [key in Key]: string | Tag[] }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

/***************************************************************
 * ヘッダー - レイアウト
 **************************************************************/
const Head = (props: HeadTableProps) => {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof ContractSearchResponse) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((header, index: number) => (
          <TableCell key={index}>
            {header.isSort ? (
              <TableSortLabel
                active={orderBy === header.key}
                direction={orderBy === header.key ? order : "asc"}
                onClick={createSortHandler(header.key)}
              >
                {header.value}
              </TableSortLabel>
            ) : (
              header.value
            )}
          </TableCell>
        ))}
        <TableCell key={headCells.length}></TableCell>
      </TableRow>
    </TableHead>
  );
};

/***************************************************************
 * 一覧 - レイアウト
 **************************************************************/
type Props = { contractList: ContractSearchResponse[] };
export const ContractTable: VFC<Props> = ({ contractList: contractList }) => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof ContractSearchResponse>("id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof ContractSearchResponse
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  /***************************************************************
   * ページング処理
   **************************************************************/
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - contractList.length) : 0;
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /***************************************************************
   * 詳細
   **************************************************************/
  // 詳細ボタン押下
  const detailBtnClick = (id: string) => {
    sessionStorage.setItem("CONTRACT_ID", id);
    navigate(ApplicationProtectedRouting.contractDetail.path());
  };

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer>
        <Table>
          <Head
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {stableSort(contractList, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((contract, index) => {
                return (
                  <TableRow key={index}>
                    {/* <TableCell component="th" scope="row" padding="none"> */}
                    <TableCell>{contract.disclosureCode}</TableCell>
                    <TableCell>{contract.tenantName}</TableCell>
                    <TableCell>{contract.companyName}</TableCell>
                    {/* <TableCell>{contract.organizationName}</TableCell> */}
                    <TableCell>{contract.contractDevice}</TableCell>
                    <TableCell>{contract.contractPeople}</TableCell>
                    <TableCell>{contract.contractPrice}</TableCell>
                    <TableCell>{Utility.dateFormat(contract.contractStart)}</TableCell>
                    <TableCell>{Utility.dateFormat(contract.contractEnd)}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        onClick={() => detailBtnClick(contract.id)}
                      >
                        詳細
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            {emptyRows > 0 && (
              <TableRow>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[15, 30, 50]}
        component="div"
        count={contractList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};
